import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import ReactMarkdown from "react-markdown";
import { ArrowClockwise, Briefcase, Info, CheckCircle } from "@phosphor-icons/react";
import { ResetConfirm } from "./IQTestCoachPanel";

/**
 * Job-coach view of a coachee's Beroepentest (profession test) chatbot.
 * Read-only transcript + reset button. Mirrors the Conflict panel pattern.
 */
export default function ProfessionTestCoachPanel({ coacheeId, coacheeName }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState("");

  const fetchSession = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/coachees/${coacheeId}/profession-test`);
      setSession(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchSession(); }, [coacheeId]);

  const handleReset = async () => {
    setResetting(true);
    setError("");
    try {
      await api.post(`/coachees/${coacheeId}/profession-test/reset`);
      setConfirmOpen(false);
      await fetchSession();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setResetting(false); }
  };

  const messages = session?.messages || [];
  const used = session?.user_message_count ?? 0;
  const cap = session?.max_user_messages ?? 50;
  const completed = session?.completed === true;
  const state = completed ? "completed" : messages.length > 0 ? "in_progress" : "not_started";

  return (
    <div className="space-y-4 max-w-3xl" data-testid="profession-coach-panel">
      <div className="neuro-card-bold p-5 bg-white">
        <div className="flex items-start gap-3">
          <Briefcase size={26} weight="duotone" className="text-[#1E88E5] shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-display text-lg font-bold tracking-tight">Beroepentest</h3>
            <p className="text-sm text-zinc-600 mt-1 leading-relaxed">
              {coacheeName || "De coachee"} zoekt via een AI-gesprek naar passende beroepen (max {cap} berichten).
            </p>
            <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-xs text-zinc-500" data-testid="profession-coach-counter">
                Status: <strong className="text-zinc-900">{stateLabel(state)}</strong>
                {state !== "not_started" && (
                  <span> — {used} / {cap} berichten verstuurd</span>
                )}
              </div>
              <button
                type="button"
                onClick={() => setConfirmOpen(true)}
                disabled={resetting || state === "not_started"}
                data-testid="profession-coach-reset-btn"
                className="inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] transition-colors disabled:bg-zinc-400"
              >
                <ArrowClockwise size={16} /> Test resetten
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="border border-[#E53935]/30 bg-[#E53935]/5 text-[#E53935] text-sm px-4 py-3 rounded-sm" data-testid="profession-coach-error">{error}</div>
      )}

      {loading ? (
        <div className="text-zinc-500 text-sm">Laden...</div>
      ) : state === "not_started" ? (
        <div className="neuro-card p-6 bg-white" data-testid="profession-coach-empty">
          <div className="flex items-start gap-3">
            <Info size={22} className="text-[#1E88E5] shrink-0 mt-0.5" />
            <div>
              <h4 className="font-display text-base font-bold tracking-tight">Nog niet gestart</h4>
              <p className="text-sm text-zinc-600 mt-1">
                {coacheeName || "De coachee"} heeft de Beroepentest nog niet gestart.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <>
          {completed && (
            <div className="neuro-card p-4 bg-[#43A047]/5 border-[#43A047]/30" data-testid="profession-coach-completed-banner">
              <div className="flex items-start gap-3">
                <CheckCircle size={22} className="text-[#43A047] shrink-0 mt-0.5" />
                <div className="text-sm text-zinc-700">
                  Beroepentest afgerond{session?.completed_at ? ` op ${new Date(session.completed_at).toLocaleString()}` : ""}. Het eindrapport is het laatste AI-bericht hieronder.
                </div>
              </div>
            </div>
          )}
          <div className="neuro-card p-4 bg-white max-h-[480px] overflow-y-auto space-y-3" data-testid="profession-coach-thread">
            {messages.map((m, i) => (
              <Bubble key={m.ts ? `${m.ts}-${m.role}` : `i-${i}`} role={m.role} content={m.content} />
            ))}
          </div>
        </>
      )}

      {confirmOpen && (
        <ResetConfirm
          onCancel={() => setConfirmOpen(false)}
          onConfirm={handleReset}
          busy={resetting}
          coacheeName={coacheeName}
          testName="Beroepentest"
          testid="profession-coach"
        />
      )}
    </div>
  );
}

function stateLabel(state) {
  if (state === "completed") return "Afgerond";
  if (state === "in_progress") return "Bezig";
  return "Nog niet gestart";
}

function Bubble({ role, content }) {
  const isUser = role === "user";
  return (
    <div className={"flex " + (isUser ? "justify-end" : "justify-start")}>
      <div
        className={
          "max-w-[80%] px-3.5 py-2.5 rounded-sm text-sm whitespace-pre-wrap leading-relaxed " +
          (isUser
            ? "bg-zinc-900 text-white"
            : "bg-zinc-100 text-zinc-900 border border-zinc-200")
        }
      >
        {isUser ? content : (
          <div className="prose prose-sm max-w-none prose-zinc prose-headings:font-display prose-headings:tracking-tight prose-p:my-2 prose-li:my-0">
            <ReactMarkdown>{content}</ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
}
